import "./index.css";
import { Toaster } from "./components/ui/toaster";
import { Toaster as Sonner } from "./components/ui/sonner";
import { TooltipProvider } from "./components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "./contexts/AuthContext";
import Auth from "./pages/Auth";
import PortalSelection from "./pages/PortalSelection";
import NotFound from "./pages/NotFound";
import StudentDashboard from "./pages/student/StudentDashboard";
import StudentRegistration from "./pages/student/StudentRegistration";
import StudentRegistrationSuccess from "./pages/student/StudentRegistrationSuccess";
import HostelAdminDashboard from "./pages/HostelAdminDashboard";

import HostelOnboarding from "./pages/HostelOnboarding";
import InstitutionOnboarding from "./pages/InstitutionOnboarding";
import InstitutionAdminDashboard from "./pages/InstitutionAdminDashboard";
import DonorDashboard from "./pages/DonorDashboard";
import TrusteeDashboard from "./pages/TrusteeDashboard";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import ImpactReports from "./pages/donor/ImpactReports";
import TaxReceipts from "./pages/donor/TaxReceipts";
import VolunteerSign from "./pages/donor/VolunteerSign";
import TrusteeInstitutions from "./pages/trustee/Institutions";
import TrusteeScholarships from "./pages/trustee/Scholarships";
import TrusteeLoans from "./pages/trustee/Loans";
import TrusteeNutrition from "./pages/trustee/Nutrition";
import TrusteeResources from "./pages/trustee/Resources";
import TrusteeHelp from "./pages/trustee/Help";
import HostelHostels from "./pages/hostel/Hostels";
import HostelScholarships from "./pages/hostel/Scholarships";
import HostelFunding from "./pages/hostel/Funding";
import HostelReports from "./pages/hostel/Reports";
import HostelCMS from "./pages/hostel/CMS";
import HostelCRM from "./pages/hostel/CRM";
import HostelSettings from "./pages/hostel/Settings";

const queryClient = new QueryClient();

const PrefixedRoutes = () => {
  return (
    <Routes>
      {/* Redirect root to portal selection */}
      <Route path="/" element={<Navigate to="/portal-selection" replace />} />
      <Route path="/portal-selection" element={<PortalSelection />} />
      <Route path="/auth" element={<Auth />} />

      {/* Hostel Registration Flow */}
      <Route
        path="/hostel/register"
        element={<Navigate to="/auth?portal=hostel" replace />}
      />
      <Route path="/hostel/onboarding" element={<HostelOnboarding />} />

      {/* Institution Registration Flow */}
      <Route
        path="/institution/register"
        element={<Navigate to="/auth?portal=institution" replace />}
      />
      <Route
        path="/institution/onboarding"
        element={<InstitutionOnboarding />}
      />

      {/* Student Portal */}
      <Route path="/student/dashboard" element={<StudentDashboard />} />
      <Route path="/dashboard/student" element={<StudentDashboard />} />
      <Route path="/student/register" element={<StudentRegistration />} />
      <Route
        path="/student/registration-success"
        element={<StudentRegistrationSuccess />}
      />

      {/* Hostel Admin Portal */}
      <Route
        path="/dashboard/hostel-admin"
        element={<HostelAdminDashboard />}
      />
      <Route path="/hostels" element={<HostelHostels />} />
      <Route path="/scholarships" element={<HostelScholarships />} />
      <Route path="/funding" element={<HostelFunding />} />
      <Route path="/reports" element={<HostelReports />} />
      <Route path="/cms" element={<HostelCMS />} />
      <Route path="/crm" element={<HostelCRM />} />
      <Route path="/settings" element={<HostelSettings />} />

      {/* Institution Admin Portal */}
      <Route
        path="/dashboard/institution-admin"
        element={<InstitutionAdminDashboard />}
      />
      <Route
        path="/institution-admin/*"
        element={<InstitutionAdminDashboard />}
      />

      {/* Donor Portal */}
      <Route path="/dashboard/donor" element={<DonorDashboard />} />
      <Route path="/donor/impact-reports" element={<ImpactReports />} />
      <Route path="/donor/tax-receipts" element={<TaxReceipts />} />
      <Route path="/donor/volunteer" element={<VolunteerSign />} />

      {/* Trustee Portal */}
      <Route path="/dashboard/trustee" element={<TrusteeDashboard />} />
      <Route path="/trustee/institutions" element={<TrusteeInstitutions />} />
      <Route path="/trustee/scholarships" element={<TrusteeScholarships />} />
      <Route path="/trustee/loans" element={<TrusteeLoans />} />
      <Route path="/trustee/nutrition" element={<TrusteeNutrition />} />
      <Route path="/trustee/resources" element={<TrusteeResources />} />
      <Route path="/trustee/help" element={<TrusteeHelp />} />

      {/* Super Admin Portal */}
      <Route path="/dashboard/super-admin" element={<SuperAdminDashboard />} />
      <Route path="/super-admin/*" element={<SuperAdminDashboard />} />

      {/* Catch-all */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

// Application root component with AuthProvider wrapping all routes
const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <AuthProvider>
          <PrefixedRoutes />
        </AuthProvider>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
